/**
 * Error handling utility for user-friendly error messages
 * Maps technical/system errors to helpful user messages
 */

import { ApiError } from '../api/http';

/**
 * Structural view of the error objects this module can receive that are not
 * `ApiError` instances: raw `fetch()` network failures, native `Error`s, and
 * legacy Firebase-era `{ code, message }` shapes. All three carry a subset of
 * these string fields; anything else is treated via `String(error)`.
 */
interface ErrorLike {
  code?: string;
  message?: string;
  stack?: string;
}

/**
 * Shared message for the "someone else already handled this pass" 409
 * scenario (approve/deny/cancel/return all guard on the pass's current
 * status server-side, so a concurrent action from another teacher — or
 * the pass simply having already changed state — surfaces here the same
 * way regardless of which action the caller attempted).
 */
export const PASS_ALREADY_UPDATED_MESSAGE =
  'This pass was already handled by someone else.';

const ERROR_MESSAGES: Record<string, string> = {
  // Network errors
  'Failed to fetch': 'Network error. Please check your internet connection.',
  NetworkError: 'Network error. Please check your internet connection.',
  ERR_NETWORK: 'Network error. Please check your internet connection.',

  // Specific business logic errors
  'already-approved': 'This email is already approved.',
  'not-approved':
    'Your email is not on the approved list. Contact your administrator.',
  'pass-limit-exceeded':
    'You have reached the maximum number of passes for today.',
  'invalid-schedule': 'No classes are currently in session.',
  'duplicate-pass': 'You already have an active pass request.',
};

// Common error patterns that need special handling
const ERROR_PATTERNS: ReadonlyArray<{ pattern: RegExp; message: string }> = [
  {
    pattern: /network/i,
    message: 'Network error. Please check your internet connection.',
  },
  {
    pattern: /timeout/i,
    message: 'The request timed out. Please try again.',
  },
  {
    pattern: /offline/i,
    message: 'You appear to be offline. Please check your internet connection.',
  },
  {
    pattern: /401|unauthorized/i,
    message: 'Your session has expired. Please sign in again.',
  },
  {
    pattern: /403|forbidden/i,
    message: "You don't have permission to perform this action.",
  },
  {
    pattern: /404|not found/i,
    message: 'The requested information could not be found.',
  },
  {
    pattern: /500|internal/i,
    message: 'A system error occurred. Please try again later.',
  },
];

/**
 * Legacy (Firebase-era) fallback matcher: maps a non-ApiError error object
 * to a user-friendly message via the ERROR_MESSAGES/ERROR_PATTERNS tables
 * above. Not exported — this is only reachable through getContextualError's
 * non-ApiError branch below, which is the single public entry point every
 * caller now uses. Still needed for real inputs getContextualError can
 * receive that aren't ApiError instances: raw `fetch()` network failures
 * (thrown before http.js's apiFetch can wrap them in an ApiError) and other
 * generic JS/runtime errors.
 * @param error - The error to process
 * @param defaultMessage - Default message if no mapping found
 * @returns User-friendly error message
 */
const getLegacyErrorMessage = (
  error: unknown,
  defaultMessage = 'An error occurred. Please try again.'
): string => {
  // Handle null/undefined
  if (!error) {
    return defaultMessage;
  }

  // Extract error message/code
  let errorCode = '';
  let errorMessage = '';

  if (typeof error === 'string') {
    errorMessage = error;
    errorCode = error;
  } else {
    const e = error as ErrorLike;
    if (e.code) {
      errorCode = e.code;
      errorMessage = e.message || '';
    } else if (e.message) {
      errorMessage = e.message;
      errorCode = e.message;
    }
  }

  // PRESERVE detailed CSV validation messages - return them as-is
  if (
    errorMessage.includes('CSV Format Error') ||
    errorMessage.includes('CSV Validation Error') ||
    errorMessage.includes('CSV Size Error') ||
    errorMessage.includes('CSV validation failed') ||
    errorMessage.includes('Upload failed') ||
    errorMessage.includes('CSV header must be exactly')
  ) {
    return errorMessage;
  }

  // Check for exact match in error messages
  if (ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  // Check for patterns in error message
  for (const { pattern, message } of ERROR_PATTERNS) {
    if (pattern.test(errorMessage)) {
      return message;
    }
  }

  // Check if error message contains a known error code
  for (const [code, message] of Object.entries(ERROR_MESSAGES)) {
    if (errorMessage.includes(code)) {
      return message;
    }
  }

  // In development, show the actual error for debugging
  if (process.env.NODE_ENV === 'development' && errorMessage) {
    console.error('Unmapped error:', error);
    return `Debug: ${errorMessage}`;
  }

  // Return default message for production
  return defaultMessage;
};

/**
 * Check if error is a network error
 * @param error - The error to check
 * @returns True if network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error) return false;

  const e = error as ErrorLike;
  const errorString = e.message || e.code || String(error);
  return /network|offline|fetch|ERR_NETWORK|ERR_INTERNET/i.test(errorString);
};

/**
 * Check if error is a permission error
 * @param error - The error to check
 * @returns True if permission error
 */
export const isPermissionError = (error: unknown): boolean => {
  if (!error) return false;

  if (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return true;
  }

  const e = error as ErrorLike;
  const errorString = e.message || e.code || String(error);
  return /permission|denied|unauthorized|forbidden|401|403/i.test(errorString);
};

/**
 * Format error for specific contexts. This is the single exported entry
 * point for turning any error this app can throw/catch into a
 * user-friendly message — it replaces what used to be two parallel
 * pipelines (see docs/AUDIT_2026-08-01.md).
 *
 * hallpass-backend's REST APIs (studentPassesApi.js and friends) throw
 * ApiError{status, message, errors} — the backend already writes
 * student-friendly {message} bodies for business-rule failures, so we
 * surface those verbatim rather than running them through the
 * Firebase-era ERROR_MESSAGES/ERROR_PATTERNS lookup, which doesn't know
 * about them. Callers that can receive a 409 (wrong-state transition)
 * already intercept it inline before reaching this function. Any other
 * error (a raw `fetch()` network failure, or another generic JS error)
 * falls back to the legacy lookup tables, matching this app's pre-backend
 * Firebase-era error shapes.
 *
 * @param error - The error to format
 * @param context - The context (e.g., 'login', 'pass-request', 'admin', 'teacher')
 * @param defaultMessage - Default message if the non-ApiError fallback has no mapping
 * @returns Context-specific error message
 */
export const getContextualError = (
  error: unknown,
  context?: string,
  defaultMessage?: string
): string => {
  let baseMessage: string;

  if (error instanceof ApiError) {
    baseMessage = error.message;
  } else {
    baseMessage = getLegacyErrorMessage(error, defaultMessage);
  }

  // Add context-specific help text
  switch (context) {
    case 'login':
      if (isNetworkError(error)) {
        return (
          baseMessage + " Make sure you're connected to the school network."
        );
      }
      if (isPermissionError(error)) {
        return 'Your account is not authorized. Please contact your teacher.';
      }
      break;

    case 'pass-request':
      if (isPermissionError(error)) {
        return 'You cannot request a pass at this time. Please see your teacher.';
      }
      break;

    case 'admin':
      if (isPermissionError(error)) {
        return 'Admin access required. Please sign in with an administrator account.';
      }
      break;

    case 'teacher':
      if (isPermissionError(error)) {
        return 'Teacher access required. Please sign in with a teacher account.';
      }
      break;

    default:
      // No additional context-specific formatting needed
      break;
  }

  return baseMessage;
};

/**
 * Log errors with enhanced console output for debugging
 * @param error - Error object or string
 * @param errorType - Type of error (auth_token, firestore_connection, etc.)
 * @param context - Context where error occurred
 * @param metadata - Additional metadata
 */
export const logError = (
  error: unknown,
  errorType: string = 'client_system',
  context: string = '',
  metadata: Record<string, unknown> = {}
): void => {
  const err = error as ErrorLike | null | undefined;

  // Never log the full URL: `window.location.href` carries the query string
  // and hash, which can hold still-redeemable secrets (e.g. a password-reset
  // token on `/reset-password?token=…`). Log origin + pathname only, so no
  // secret ever reaches console output captured by extensions, screen-shares,
  // support tickets, or log forwarding.
  const sanitizedUrl =
    typeof window !== 'undefined' && window.location
      ? window.location.origin + window.location.pathname
      : '';

  // Sanitized error details, safe to surface in any environment.
  const errorDetails = {
    type: errorType,
    context: context,
    message: err?.message || err?.toString() || 'Unknown error',
    timestamp: new Date().toISOString(),
    url: sanitizedUrl,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    ...metadata,
  };

  // In production (and any non-development environment) forward ONLY the
  // sanitized payload: no raw error object (it can carry backend response
  // bodies with student PII) and no query string / hash (secrets). This is
  // deliberately gated so verbose, PII-bearing output can never leak from a
  // deployed build.
  if (process.env.NODE_ENV !== 'development') {
    console.error(
      `🚨 Error [${errorType}] in ${context || 'unknown context'}:`,
      errorDetails
    );
    return;
  }

  // Development only: rich grouped logs (raw error object + full stack) for
  // the best debugging experience. Never reached outside development.
  console.group(`🚨 Error [${errorType}] in ${context || 'unknown context'}`);
  console.error('Message:', errorDetails.message);

  if (err?.stack) {
    console.error('Stack trace:', err.stack);
  }

  if (error && typeof error === 'object') {
    console.error('Full error object:', error);
  }

  console.error('Context details:', errorDetails);
  console.groupEnd();
};
